import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import type { IRamadanRiddleQuestionBanner } from "@types";
import ramadanRiddlesApi from "@api/ramadanRiddlesApi";
import { useRiddlesActions } from "../RamadanRiddlesContext";

type RamadanRiddlesQuestionBannerProps = {
    banner?: IRamadanRiddleQuestionBanner | null;
    onStartQuestion: () => void;
};

const getRemainingSeconds = (banner?: IRamadanRiddleQuestionBanner | null) => {
    if (!banner) return 0;
    const { remaining_hours, remaining_minutes, client_timestamp } = banner;
    if (remaining_hours == null || remaining_minutes == null) return 0;
    const totalSeconds = Math.max(0, Math.floor(remaining_hours * 3600 + remaining_minutes * 60));
    if (totalSeconds === 0) return 0;
    const elapsedSeconds = Math.max(
        0,
        Math.floor((Date.now() - (client_timestamp ?? Date.now())) / 1000)
    );
    return Math.max(0, totalSeconds - elapsedSeconds);
};

const getTotalSeconds = (banner?: IRamadanRiddleQuestionBanner | null) => {
    if (!banner) return 0;
    const { remaining_hours, remaining_minutes } = banner;
    if (remaining_hours == null || remaining_minutes == null) return 0;
    return Math.max(0, Math.floor(remaining_hours * 3600 + remaining_minutes * 60));
};

const RamadanRiddlesQuestionBanner: React.FC<RamadanRiddlesQuestionBannerProps> = ({
    banner,
    onStartQuestion,
}) => {
    const t = useTranslations();
    const { setOverview, setOverviewError, setOverviewLoading } = useRiddlesActions();
    const [remainingSeconds, setRemainingSeconds] = useState(() => getRemainingSeconds(banner));
    const isLocked = remainingSeconds > 0;
    const hasRefetchedRef = useRef(false);
    const isRefetchingRef = useRef(false);

    const fetchOverview = useCallback(async () => {
        if (isRefetchingRef.current) return;
        isRefetchingRef.current = true;
        setOverviewError(null);
        setOverviewLoading(true);

        try {
            const response = await ramadanRiddlesApi.getOverview().fetch();
            if (response?.meta?.code === 200) {
                setOverview(response.data);
            } else {
                const message = response?.meta?.msg || t("client_general_error");
                setOverviewError(message);
                toast.error(message);
            }
        } catch (error) {
            console.error("Failed to refresh Ramadan riddle overview:", error);
            setOverviewError(t("client_general_error"));
            toast.error(t("client_general_error"));
        } finally {
            setOverviewLoading(false);
            isRefetchingRef.current = false;
        }
    }, [setOverview, setOverviewError, setOverviewLoading, t]);

    useEffect(() => {
        hasRefetchedRef.current = false;
    }, [banner?.client_timestamp, banner?.remaining_hours, banner?.remaining_minutes]);

    useEffect(() => {
        const updateRemaining = () => {
            setRemainingSeconds(getRemainingSeconds(banner));
        };

        updateRemaining();
        const totalSeconds = getTotalSeconds(banner);
        const initialRemaining = getRemainingSeconds(banner);
        if (totalSeconds <= 0 || initialRemaining <= 0) return;

        const intervalId = setInterval(() => {
            const nextRemaining = getRemainingSeconds(banner);
            setRemainingSeconds(nextRemaining);
            if (nextRemaining <= 0) {
                clearInterval(intervalId);
                if (!hasRefetchedRef.current) {
                    hasRefetchedRef.current = true;
                    void fetchOverview();
                }
            }
        }, 60_000);

        return () => clearInterval(intervalId);
    }, [banner, fetchOverview]);

    const bannerTitle = banner?.title ?? t("Today's Question");
    const startQuestionLabel = banner?.description ?? t("Start today's riddle");
    const countdownLabel = useMemo(() => {
        const totalMinutes = Math.max(0, Math.ceil(remainingSeconds / 60));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, "0")}H ${String(minutes).padStart(2, "0")}M`;
    }, [remainingSeconds]);
    const bannerSubtitle = isLocked
        ? countdownLabel
        : banner?.subtitle ?? t("Ready for today's riddle?");
    const actionLabel = startQuestionLabel;

    const handleStartQuestion = () => {
        if (isLocked) return;
        onStartQuestion();
    };

    return (
        <button
            type="button"
            onClick={handleStartQuestion}
            //   disabled={isLocked}
            aria-disabled={isLocked}
            className="mt-6 w-full rounded-2xl bg-[#F27D23] px-4 py-4 text-start text-white bg-[url('/svgs/ramadan-bg4.svg')] disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={actionLabel}
        >
            <div className="flex items-center gap-4">
                <div className="flex-1">
                    <p className="text-xs font-bold uppercase text-white/80">{bannerTitle}</p>
                    <p className="mt-1 text-lg font-extrabold text-[#FDE040]">{bannerSubtitle}</p>
                    <p className="mt-2 text-sm font-bold uppercase text-white">{actionLabel}</p>
                </div>
                <Image
                    src="/imgs/dabdoob-unpacking-gift.webp"
                    alt={t("Gift")}
                    width={80}
                    height={80}
                    className="h-16 w-16 object-contain"
                />
            </div>
        </button>
    );
};

export default RamadanRiddlesQuestionBanner;
